// app/javascript/controllers/sleep_layers.js
import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["map"]; // Définition du target "map"
  static values = {
    wayId: String, // ID du way
    waySlug: String, // Slug du way
    activeTraceId: Number, // ID de la trace active
  };

  initialize() {
    this.layersInitialized = false;
    this.tracesData = null; // Stockage des données GeoJSON
    this.handleMapInitialized = this.handleMapInitialized.bind(this);
    this.handleTurboLoad = this.handleTurboLoad.bind(this);
  }

  connect() {
    this.loadAttributesFromDOM();
    
    // Charge les données une seule fois au démarrage
    if (!this.tracesData) {
      this.loadWayTracks();
    }

    // Écoute l'initialisation de la carte
    document.addEventListener("map-initialized", this.handleMapInitialized);
    document.addEventListener('turbo:load', this.handleTurboLoad);
  }

  disconnect() {
    // Supprime les écouteurs d'événements
    document.removeEventListener("map-initialized", this.handleMapInitialized);
    document.removeEventListener('turbo:load', this.handleTurboLoad);

    // Nettoie les sources et layers si la carte existe
    if (this.map) {
      this.cleanupLayers();
    }
    this.layersInitialized = false;
  }

  cleanupLayers() {
    const layers = ['way-paths-inactive', 'way-paths-active', 'way-paths-all', 'way-paths-fill'];
    const sources = ['all-paths', 'active-paths', 'inactive-paths'];

    // Supprime d'abord les layers
    layers.forEach(layer => {
      if (this.map.getLayer(layer)) {
        this.map.removeLayer(layer);
      }
    });

    // Puis supprime les sources
    sources.forEach(source => {
      if (this.map.getSource(source)) {
        this.map.removeSource(source);
      }
    });
  }

  loadAttributesFromDOM() {
    const wayIdFromAttribute = this.element.getAttribute("data-sleep-layers-way-id");
    if (wayIdFromAttribute) {
      this.wayIdValue = wayIdFromAttribute;
    }

    const activeTraceIdFromAttribute = this.element.getAttribute("data-sleep-layers-active-trace-id");
    if (activeTraceIdFromAttribute) {
      this.activeTraceIdValue = parseInt(activeTraceIdFromAttribute, 10);
    }

    const waySlugFromAttribute = this.element.getAttribute("data-sleep-layers-way-slug");
    if (waySlugFromAttribute) {
      this.waySlugValue = waySlugFromAttribute;
    }
  }

  handleMapInitialized(event) {
    this.map = event.detail.map;
    if (!this.map) {
      console.error("La carte n'a pas pu être initialisée.");
      return;
    }

    // Attendre que le style soit complètement chargé
    if (!this.map.isStyleLoaded()) {
      this.map.once('style.load', () => {
        // Si on a déjà les données, on initialise les layers
        if (this.tracesData) {
          this.initializeLayers();
        } else {
          // Sinon on charge les données
          this.loadWayTracks();
        }
      });
    } else {
      // Le style est déjà chargé
      if (this.tracesData) {
        this.initializeLayers();
      } else {
        this.loadWayTracks();
      }
    }
  }

  handleTurboLoad() {
    console.log("🔄 Turbo load - Mise à jour des layers");
    const oldWayId = this.wayIdValue;
    this.loadAttributesFromDOM();
    
    // Ne recharge que si nécessaire
    if (this.wayIdValue && this.map && 
        (oldWayId !== this.wayIdValue || !this.layersInitialized)) {
      console.log("🔄 Rechargement des tracés après Turbo load");
      this.cleanupLayers();
      this.initializeLayers();
    }
  }

  initializeLayers() {
    if (!this.map || !this.tracesData || this.layersInitialized) return;

    // Vérifier si la carte est prête à recevoir des layers
    if (!this.map.isStyleLoaded()) {
      console.log("🛣 Carte pas encore prête, attente du chargement du style...");
      setTimeout(() => {
        if (this.map && !this.layersInitialized) {
          console.log("🛣 Nouvelle tentative d'initialisation des layers...");
          this.initializeLayers();
        }
      }, 100);
      return;
    }

    console.log("🛣 Initialisation des layers...");
    
    try {
      this.addWayTracksToMap(this.tracesData.features);
      
      // Ajoute le curseur pointer sur la zone de clic élargie
      this.map.on('mouseenter', 'way-paths-fill', () => {
        this.map.getCanvas().style.cursor = 'pointer';
      });

      this.map.on('mouseleave', 'way-paths-fill', () => {
        this.map.getCanvas().style.cursor = '';
      });

      this.layersInitialized = true;
      console.log("✅ Layers initialisés avec succès");
    } catch (error) {
      console.error("❌ Erreur lors de l'initialisation des layers:", error);
    }
  }

  initializeTraceInteractions() {
    // Change le curseur au survol des traces inactives
    this.map.on('mouseenter', 'traces-inactive', () => {
      this.map.getCanvas().style.cursor = 'pointer';
    });

    this.map.on('mouseleave', 'traces-inactive', () => {
      this.map.getCanvas().style.cursor = '';
    });

    // Effet de surbrillance au survol
    this.map.on('mousemove', 'traces-inactive', (e) => {
      if (e.features.length > 0) {
        this.map.setPaintProperty('traces-inactive', 'line-color', [
          'case',
          ['==', ['get', 'id'], e.features[0].properties.id],
          '#64748B', // Couleur au survol
          '#94A3B8'  // Couleur normale
        ]);
      }
    });

    this.map.on('mouseleave', 'traces-inactive', () => {
      this.map.setPaintProperty('traces-inactive', 'line-color', '#94A3B8');
    });

    // Optionnel : Ajouter un clic pour activer la trace
    this.map.on('click', 'traces-inactive', (e) => {
      if (e.features.length > 0) {
        const traceId = e.features[0].properties.id;
        // Ici vous pouvez ajouter la logique pour activer la trace
        console.log(`Trace ${traceId} cliquée`);
      }
    });
  }

  loadWayTracks() {
    if (!this.wayIdValue) return;

    // Ajoute un indicateur de chargement
    const mapContainer = this.map?.getContainer();
    if (mapContainer) {
      mapContainer.style.opacity = '0.5';
      mapContainer.style.transition = 'opacity 0.3s';
    }

    console.log("🛣 Chargement initial des traces...");
    fetch(`/json_ways/${this.wayIdValue}.json`)
      .then(response => response.json())
      .then(data => {
        if (!data || !data.features || !Array.isArray(data.features)) {
          console.error("❌ Les données reçues ne sont pas au format GeoJSON valide.");
          return;
        }
        
        console.log("✅ Données des traces chargées");
        this.tracesData = data;
        
        // Si la carte est déjà prête, on initialise les layers
        if (this.map) {
          this.initializeLayers();
          // Restaure l'opacité
          if (mapContainer) {
            mapContainer.style.opacity = '1';
          }
        }
      })
      .catch(error => {
        console.error("❌ Erreur lors du chargement des tracés :", error);
        // Restaure l'opacité en cas d'erreur
        if (mapContainer) {
          mapContainer.style.opacity = '1';
        }
      });
  }

  addWayTracksToMap(features) {
    // Encapsule les features dans un objet GeoJSON complet
    const geojson = {
      type: "FeatureCollection",
      features: features,
    };

    // Ajoute ou met à jour la source GeoJSON pour tous les tracés
    if (!this.map.getSource("all-paths")) {
      this.map.addSource("all-paths", {
        type: "geojson",
        data: geojson,
      });
    } else {
      this.map.getSource("all-paths").setData(geojson);
    }

    // Ajoute le layer pour tous les tracés
    if (!this.map.getLayer("way-paths-all")) {
      this.map.addLayer({
        id: "way-paths-all",
        type: "line",
        source: "all-paths",
        layout: {"line-cap": "round", "line-join": "round"},
        paint: {"line-color": "#1F2648", "line-width": 7, "line-opacity": 1},
      });
    }

    // Ajoute une couche invisible pour faciliter les clics
    if (!this.map.getLayer("way-paths-fill")) {
      this.map.addLayer({
        id: "way-paths-fill",
        type: "line",
        source: "all-paths",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: {
          "line-color": "transparent", // Invisible
          "line-width": 15, // Largeur plus grande pour faciliter les clics
        },
      });
    }

    // Filtrer les traces actives et inactives
    const activeFeatures = features.filter(feature =>
        feature.properties.traces.includes(this.activeTraceIdValue)
    );
    const inactiveFeatures = features.filter(feature =>
        !feature.properties.traces.includes(this.activeTraceIdValue)
    );

    // Crée une source pour les traces actives
    if (!this.map.getSource("active-paths")) {
      this.map.addSource("active-paths", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: activeFeatures,
        },
      });
    } else {
      this.map.getSource("active-paths").setData({
        type: "FeatureCollection",
        features: activeFeatures,
      });
    }

    // Ajoute le layer pour les traces actives
    if (!this.map.getLayer("way-paths-active")) {
      this.map.addLayer({
        id: "way-paths-active",
        type: "line",
        source: "active-paths",
        layout: {"line-cap": "round", "line-join": "round"},
        paint: {"line-color": "#FAB313", "line-width": 4, "line-opacity": 1},
      });
    }

    // Crée une source pour les traces inactives
    if (!this.map.getSource("inactive-paths")) {
      this.map.addSource("inactive-paths", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: inactiveFeatures,
        },
      });
    } else {
      this.map.getSource("inactive-paths").setData({
        type: "FeatureCollection",
        features: inactiveFeatures,
      });
    }

    // Ajoute le layer pour les traces inactives
    if (!this.map.getLayer("way-paths-inactive")) {
      this.map.addLayer({
        id: "way-paths-inactive",
        type: "line",
        source: "inactive-paths",
        layout: {"line-cap": "round", "line-join": "round"},
        paint: {
          "line-color": "#CBD5E1",
          "line-width": 4,
          "line-opacity": 1,
          "line-dasharray": [4, 3], // Style pointillé
        },
      });
    }

    this.map.on("click", "way-paths-fill", (event) => {
      const features = this.map.queryRenderedFeatures(event.point, { layers: ["way-paths-fill"] });

      if (features.length > 0) {
        const clickedTrackId = features[0].properties.id; // Récupère l'ID du track cliqué

        // Construit la nouvelle URL
        const currentUrl = new URL(window.location.href);

        // Extrait les segments actuels
        const pathSegments = currentUrl.pathname.split("/");
        const baseIndex = pathSegments.indexOf("hebergements");

        // Reconstruit le chemin en respectant l'ordre souhaité
        let newPath = `/${this.waySlugValue}/hebergements`;

        // Ajoute "from" si présent
        if (pathSegments[baseIndex + 1] === "from" && pathSegments[baseIndex + 2]) {
          newPath += `/from/${pathSegments[baseIndex + 2]}`;
        }

        // Ajoute "distance" si présent
        if (pathSegments[baseIndex + 3] === "distance" && pathSegments[baseIndex + 4]) {
          newPath += `/distance/${pathSegments[baseIndex + 4]}`;
        }

        // Ajoute "track" avec l'ID du track cliqué
        newPath += `/track/${clickedTrackId}`;

        // Nettoie les paramètres de requête pour éviter les doublons
        const searchParams = new URLSearchParams(currentUrl.search);
        searchParams.delete("track"); // Supprime "track" de la chaîne de requête
        searchParams.delete("trace"); // Supprime "trace" car il devient obsolète

        // Construit la nouvelle URL finale
        let finalUrl = newPath;
        if (searchParams.toString()) {
          finalUrl += `?${searchParams.toString()}`; // Ajoute les paramètres seulement s'ils existent
        }

        // Redirige vers la nouvelle URL
        window.location.href = finalUrl;
      }
    });
  }

  addPoiServicesLayer() {
    console.log("Ajout du layer pour les POI services...");

    // Génère des points fictifs autour du centre
    const center = [2.672035, 44.554877]; // [longitude, latitude]
    const poiServices = this.generateFictitiousPoints(center);

    // Ajoute une source GeoJSON pour les POI services
    this.map.addSource("poi-services", {
      type: "geojson",
      data: {
        type: "FeatureCollection",
        features: poiServices,
      },
    });

    // Ajoute un layer pour afficher les POI services
    this.map.addLayer({
      id: "poi-services-layer",
      type: "circle",
      source: "poi-services",
      paint: {
        "circle-radius": 6,
        "circle-color": "#007bff", // Bleu pour les POI services
      },
    });
  }

  generateFictitiousPoints(center) {
    const features = [];
    const [centerLng, centerLat] = center;

    // Génère 5 points fictifs autour du centre
    for (let i = 0; i < 5; i++) {
      const randomLng = centerLng + (Math.random() - 0.5) * 0.1; // Variation de longitude (-0.05 à +0.05)
      const randomLat = centerLat + (Math.random() - 0.5) * 0.1; // Variation de latitude (-0.05 à +0.05)

      features.push({
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [randomLng, randomLat],
        },
        properties: {
          id: i + 1,
          type: "service", // Type de POI
        },
      });
    }

    return features;
  }

  updateTraceStatus(traceId) {
    if (!this.map || !this.layersInitialized) return;

    console.log(`🛣 Mise à jour du statut de la trace ${traceId}`);
    this.activeTraceIdValue = traceId;
    
    // Filtre les features actives/inactives
    const activeFeatures = this.tracesData.features.filter(feature =>
        feature.properties.traces.includes(this.activeTraceIdValue)
    );
    const inactiveFeatures = this.tracesData.features.filter(feature =>
        !feature.properties.traces.includes(this.activeTraceIdValue)
    );

    // Met à jour les sources
    if (this.map.getSource("active-paths")) {
      this.map.getSource("active-paths").setData({
        type: "FeatureCollection",
        features: activeFeatures
      });
    }

    if (this.map.getSource("inactive-paths")) {
      this.map.getSource("inactive-paths").setData({
        type: "FeatureCollection",
        features: inactiveFeatures
      });
    }
  }
}

